import {
  OktonSearchSelect,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, Wallet } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  CENTRO_CUSTO_NOVO,
  CLIENTE_NOVO,
  DEPARTAMENTO_NOVO,
  DIRECIONADOR_NOVO,
  EnderecoConsultaCnpj,
  TIPO_CLIENTE_PF,
  TIPO_CLIENTE_PJ,
  usaControlaDRE,
  useCentrosCusto,
  useClientePorDocumento,
  useClientes,
  useConsultaCnpj,
  useCreateCentroCusto,
  useCreateCliente,
  useCreateDepartamento,
  useCreateDirecionador,
  useDepartamentos,
  useDirecionadores,
  useParametros,
  usePlanosDFC,
} from '../../../services/hooks/useCadastros';
import { formataDocumento, soDigitos } from '../../../utils/documento';
import {
  useContasBancarias,
  useVinculosDaConta,
} from '../../../services/hooks/useContasBancarias';
import {
  TIPO_FIN_PAGAR,
  TIPO_FIN_RECEBER,
  useMoedasCheque,
} from '../../../services/hooks/useLancamentosFinanceiros';
import {
  ChequeLancamento,
  useCreateLancamentoFinanceiro,
} from '../../../services/hooks/useLancamentosFinanceirosMutations';
import formatValue from '../../../utils/formatValue';
import { FiltroData } from '../../../utils/setaFiltroDiario';
import { limparEntradaValor, valorParaNumero } from '../../../utils/valorInput';

// Tipo de documento da Moeda de Cheque de Terceiro, como o IFLEX numera.
const TIPO_TITULO_CHEQUETERCEIRO = '14';

// valorParaNumero devolve NaN quando o texto não tem número: somar NaN
// contaminaria o total inteiro.
function chequeParaNumero(texto: string): number {
  const valor = valorParaNumero(texto);
  return Number.isNaN(valor) ? 0 : valor;
}

const VAZIO = {
  conta: '',
  data: new Date().toISOString().split('T')[0],
  observacao: '',
  valor: '',
  centrocusto: '',
  centrocustonovo: '',
  cliente: '',
  clientenovotipo: TIPO_CLIENTE_PF,
  clientenovonome: '',
  clientenovodocumento: '',
  clientenovoie: '',
  direcionador: '',
  direcionadornovo: '',
  direcionadorplano: '',
  departamento: '',
  departamentonovo: '',
  documento: '',
  moeda: '',
  // '1' Receber (entrada), '2' Pagar (saída). O valor vai sempre positivo:
  // o sentido do dinheiro está no tipo, como no financeiro do IFLEX.
  tipo: TIPO_FIN_RECEBER,
};

/**
 * Cadastro de um lançamento financeiro, em rota própria como as demais telas
 * de cadastro do OktonBank.
 *
 * Centro de Custo, Departamento e Direcionador podem ser criados aqui mesmo,
 * sem sair da tela: são cadastrados antes do título, porque o financeiro grava
 * o código e não o nome.
 *
 * Ficar numa rota, e não num pop-up sobre a lista, tem um efeito colateral
 * bom: a lista deixa de carregar as sete listas que só o formulário usa.
 */
interface EstadoLancamento {
  conta?: string;
  form?: Partial<typeof VAZIO>;
  cheques?: ChequeLancamento[];
  voltarPara?: '/monitor-bancario';
  periodo?: FiltroData;
}

const NovoLancamento = () => {
  const history = useHistory();
  // A lista manda a Conta que estava filtrada: quem clicou em Novo estava
  // olhando para ela, e redigitar seria trabalho repetido.
  //
  // A tela de cheques devolve o formulário inteiro no mesmo lugar: ela não
  // grava nada, e voltar de lá não pode custar o que já estava digitado.
  const { state } = useLocation<EstadoLancamento | undefined>();
  const destino =
    state?.voltarPara === '/monitor-bancario'
      ? '/monitor-bancario'
      : '/lancamentos';
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const { data: accounts = [] } = useContasBancarias();
  const { data: centrosCusto = [] } = useCentrosCusto();
  const { data: departamentos = [] } = useDepartamentos();
  const { data: direcionadores = [] } = useDirecionadores();
  const { data: planosDFC = [] } = usePlanosDFC();
  const { data: parametros } = useParametros();
  const controlaDRE = usaControlaDRE(parametros);

  const createMutation = useCreateLancamentoFinanceiro();
  const createCentroCusto = useCreateCentroCusto();
  const createDepartamento = useCreateDepartamento();
  const createDirecionador = useCreateDirecionador();
  const createCliente = useCreateCliente();

  const [form, setForm] = useState(
    state?.form
      ? { ...VAZIO, ...state.form }
      : { ...VAZIO, conta: state?.conta ?? '' },
  );
  const voltar = () => {
    if (destino === '/monitor-bancario') {
      history.push(destino, { conta: form.conta, periodo: state?.periodo });
    } else {
      history.push(destino);
    }
  };
  // Os cheques vivem só aqui até o lançamento ser gravado.
  const [cheques] = useState<ChequeLancamento[]>(state?.cheques ?? []);
  const [erro, setErro] = useState('');
  const [saving, setSaving] = useState(false);
  // Trava por ref, não só pelo estado: setSaving(true) só desabilita o botão
  // depois de um novo render, e um duplo clique bem rápido dispara os dois
  // handlers antes disso acontecer. A ref muda no mesmo instante do clique,
  // então o segundo clique já entra barrado - é o que evitava um Cliente (e
  // até o próprio lançamento) sendo cadastrado em dobro.
  const salvandoRef = useRef(false);
  const [buscaCliente, setBuscaCliente] = useState('');
  const { data: clientes = [], isFetching: isClientesFetching } =
    useClientes(buscaCliente);

  // Endereço vindo da consulta de CNPJ do cadastro rápido de Cliente: não
  // aparece na tela, só viaja junto no cadastro do Cliente quando ele é
  // salvo (é o que "cadastrando endereço automaticamente" pede).
  const [enderecoNovoCliente, setEnderecoNovoCliente] = useState<
    EnderecoConsultaCnpj | undefined
  >();
  // Uma vez que o usuário digite algo no Nome, a consulta de CNPJ nunca mais
  // sobrescreve — checar só "campo vazio" não bastava: se o usuário apagasse
  // o nome sugerido pra escrever outro do zero, o instante em que o campo
  // ficava vazio de novo era reaproveitado pela consulta pra preencher com o
  // nome da Receita outra vez.
  const nomeClienteDigitadoRef = useRef(false);
  const { data: cnpjConsultado } = useConsultaCnpj(
    form.clientenovotipo === TIPO_CLIENTE_PJ ? form.clientenovodocumento : '',
  );
  React.useEffect(() => {
    if (!cnpjConsultado?.encontrado) return;
    if (!nomeClienteDigitadoRef.current) {
      setForm(f => ({
        ...f,
        clientenovonome: cnpjConsultado.nome ?? f.clientenovonome,
      }));
    }
    setEnderecoNovoCliente(cnpjConsultado.endereco);
  }, [cnpjConsultado]);

  // Documento do "Novo Cliente" já pertence a outro Cliente cadastrado: sem
  // este aviso, o Cliente novo nem chegava a ser criado — o cadastro rápido
  // vinculava o lançamento ao Cliente já existente por baixo dos panos, sem
  // avisar que o nome digitado não era o que ficou gravado.
  const { data: clienteComDocumento } = useClientePorDocumento(
    form.cliente === CLIENTE_NOVO ? form.clientenovodocumento : '',
  );

  // As Moedas da Conta escolhida: é entre elas que se diz por onde o dinheiro
  // entrou.
  const { data: moedasDaConta = [] } = useVinculosDaConta(form.conta);
  // As de Cheque de Terceiro entram junto, independentemente da Conta: o
  // cheque é um papel em mãos e só chega a uma Conta quando for depositado.
  const { data: moedasCheque = [] } = useMoedasCheque();
  const opcoesMoeda = [
    ...moedasDaConta
      .filter(m => m.principal_ctm !== '1')
      .map(m => ({
        id: m.moeda_ctm,
        nome: m.nome_moe,
        tipodocumento: m.tipodocumento_tpt,
      })),
    ...moedasCheque
      // Uma Moeda de cheque pode estar vinculada à Conta e já ter entrado
      // na lista acima; repeti-la daria duas opções iguais no seletor.
      .filter(c => !moedasDaConta.some(m => m.moeda_ctm === c.id_moe))
      .map(c => ({
        id: c.id_moe,
        nome: c.nome_moe,
        tipodocumento: c.tipodocumento_tpt,
      })),
  ];

  // Só a Moeda de Cheque de Terceiro pede os papéis: é nela que o título é
  // liquidado por cheques que continuam em mãos até o depósito.
  const moedaEscolhida = opcoesMoeda.find(m => m.id === form.moeda);
  const ehCheque = moedaEscolhida?.tipodocumento === TIPO_TITULO_CHEQUETERCEIRO;
  const somaCheques = cheques.reduce(
    (total, c) => total + chequeParaNumero(c.valor),
    0,
  );
  const valorDoTitulo = chequeParaNumero(form.valor);
  const chequesFecham =
    cheques.length > 0 && Math.abs(somaCheques - valorDoTitulo) < 0.005;

  const salvar = async () => {
    if (salvandoRef.current) return;
    if (!form.conta || !form.data) {
      setErro('Informe a Conta e a data do lançamento.');
      return;
    }
    const valorNumerico = valorParaNumero(form.valor);

    if (!valorNumerico) {
      setErro('Informe um valor maior que zero.');
      return;
    }
    if (
      form.centrocusto === CENTRO_CUSTO_NOVO &&
      !form.centrocustonovo.trim()
    ) {
      setErro('Informe o nome do novo Centro de Custo.');
      return;
    }
    if (
      form.departamento === DEPARTAMENTO_NOVO &&
      !form.departamentonovo.trim()
    ) {
      setErro('Informe o nome do novo Departamento.');
      return;
    }
    if (
      form.direcionador === DIRECIONADOR_NOVO &&
      !form.direcionadornovo.trim()
    ) {
      setErro('Informe o nome do novo Direcionador.');
      return;
    }
    if (form.cliente === CLIENTE_NOVO) {
      if (!form.clientenovonome.trim()) {
        setErro('Informe o nome do novo Cliente.');
        return;
      }
      const documento = soDigitos(form.clientenovodocumento);
      if (form.clientenovotipo === TIPO_CLIENTE_PJ && documento.length !== 14) {
        setErro('Informe um CNPJ válido para o novo Cliente.');
        return;
      }
      if (
        form.clientenovotipo === TIPO_CLIENTE_PF &&
        documento !== '' &&
        documento.length !== 11
      ) {
        setErro('Informe um CPF válido para o novo Cliente.');
        return;
      }
      if (clienteComDocumento?.existe && clienteComDocumento.cliente) {
        setErro(
          `Este CPF/CNPJ já pertence ao Cliente "${clienteComDocumento.cliente.nome_cli}" (código ${clienteComDocumento.cliente.id_cli}). Busque por ele no campo Cliente.`,
        );
        return;
      }
    }
    if (controlaDRE && !form.direcionador) {
      setErro('Informe o Direcionador.');
      return;
    }
    if (
      controlaDRE &&
      form.direcionador === DIRECIONADOR_NOVO &&
      !form.direcionadorplano
    ) {
      setErro('Informe o Plano de Contas (DFC) do Direcionador.');
      return;
    }
    if (ehCheque && !chequesFecham) {
      setErro(
        'Cadastre os cheques: a soma deles precisa fechar com o valor do lançamento.',
      );
      return;
    }

    salvandoRef.current = true;
    setSaving(true);
    setErro('');

    try {
      // As classificações novas são cadastradas antes do título: o financeiro
      // grava o código, não o nome.
      let { centrocusto, departamento, direcionador, cliente } = form;
      if (cliente === CLIENTE_NOVO) {
        const criado = await createCliente.mutateAsync({
          tipo: form.clientenovotipo,
          nome: form.clientenovonome.trim(),
          documento: soDigitos(form.clientenovodocumento),
          ie: form.clientenovoie.trim(),
          endereco: enderecoNovoCliente,
        });
        cliente = String(criado.id_cli);
      }
      if (centrocusto === CENTRO_CUSTO_NOVO) {
        const criado = await createCentroCusto.mutateAsync(
          form.centrocustonovo.trim(),
        );
        centrocusto = String(criado.id_cdc);
      }
      if (departamento === DEPARTAMENTO_NOVO) {
        const criado = await createDepartamento.mutateAsync(
          form.departamentonovo.trim(),
        );
        departamento = String(criado.id_dpt);
      }
      if (direcionador === DIRECIONADOR_NOVO) {
        const criado = await createDirecionador.mutateAsync({
          nome: form.direcionadornovo.trim(),
          planocontas: form.direcionadorplano,
        });
        direcionador = String(criado.id_ori);
      }

      // O Caixa não é escolhido aqui: quem decide é a regra do IFLEX, pela
      // data do lançamento — Caixa aberto do usuário, ou o Caixa Geral.
      await createMutation.mutateAsync({
        conta: form.conta,
        tipo: form.tipo,
        data: form.data,
        valor: String(Math.abs(valorNumerico)),
        documento: form.documento.trim(),
        observacao: form.observacao.trim(),
        cliente,
        centrocusto,
        departamento,
        direcionador,
        moeda: form.moeda,
        // Só agora os cheques saem da memória: o título e eles entram no
        // mesmo commit, ou nenhum dos dois entra.
        cheques: ehCheque ? JSON.stringify(cheques) : '',
      });
      voltar();
    } catch (err) {
      setErro(
        err instanceof Error
          ? err.message
          : 'Erro ao salvar lançamento financeiro.',
      );
    } finally {
      salvandoRef.current = false;
      setSaving(false);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Novo Lançamento - Interno"
        subtitle="Registro interno para conciliação"
        onBack={voltar}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Box>
          {!!erro && <OktonAlert status="error" description={erro} />}

          {/* Duas colunas: o lançamento em si à esquerda, a
          classificação à direita. Em coluna única a classificação
          ficava abaixo da dobra, e ela é o que o Controla DRE exige. */}
          <SimpleGrid columns={[1, 1, 2]} spacing="8">
            <Stack spacing="4">
              <Box>
                <Text
                  as="label"
                  htmlFor="conta"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Conta *
                </Text>
                <OktonSearchSelect
                  name="conta"
                  placeholder="Selecione a conta"
                  value={form.conta}
                  onChange={valor =>
                    setForm(f => ({
                      ...f,
                      conta: valor,
                      moeda: '',
                    }))
                  }
                  options={[
                    ...accounts.map(a => ({
                      value: a.id_cts,
                      label: a.descricao_cts,
                    })),
                  ]}
                />
                {/* A Forma de Pagamento não é pedida: o título nasce liquidado,
            então o pagamento é sempre à vista. */}
                <Text fontSize="xs" color="gray.500" mt="1">
                  A Forma de Pagamento (à vista) vem da Conta.
                </Text>
              </Box>

              <SimpleGrid columns={2} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="lancamento-data"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Data *
                  </Text>
                  <OktonInput
                    name="lancamento-data"
                    type="date"
                    value={form.data}
                    color={color}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, data: valor }));
                    }}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb="1">
                    Tipo *
                  </Text>
                  <HStack overflow="hidden" spacing={2} p="1">
                    <Button
                      flex="1"
                      borderRadius="none"
                      colorScheme="green"
                      variant={
                        form.tipo === TIPO_FIN_RECEBER ? 'solid' : 'outline'
                      }
                      onClick={() =>
                        setForm(f => ({ ...f, tipo: TIPO_FIN_RECEBER }))
                      }
                    >
                      Crédito
                    </Button>
                    <Button
                      flex="1"
                      borderRadius="none"
                      colorScheme="red"
                      variant={
                        form.tipo === TIPO_FIN_PAGAR ? 'solid' : 'outline'
                      }
                      onClick={() =>
                        setForm(f => ({ ...f, tipo: TIPO_FIN_PAGAR }))
                      }
                    >
                      Débito
                    </Button>
                  </HStack>
                </Box>
              </SimpleGrid>

              <Box>
                <Text
                  as="label"
                  htmlFor="lancamento-observacao"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Observação
                </Text>
                <OktonInput
                  name="lancamento-observacao"
                  color={color}
                  placeholder="Histórico do lançamento"
                  maxLength={1024}
                  value={form.observacao}
                  onChange={e => {
                    const valor = e.target.value;
                    setForm(f => ({ ...f, observacao: valor }));
                  }}
                />
              </Box>

              <SimpleGrid columns={2} spacing="4">
                <Box>
                  <Text
                    as="label"
                    htmlFor="lancamento-valor"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Valor (R$) *
                  </Text>
                  <OktonInput
                    name="lancamento-valor"
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    color={color}
                    value={form.valor}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({
                        ...f,
                        valor: limparEntradaValor(valor),
                      }));
                    }}
                  />
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="lancamento-documento"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Referência
                  </Text>
                  <OktonInput
                    name="lancamento-documento"
                    color={color}
                    placeholder="Nº doc / NF"
                    maxLength={40}
                    value={form.documento}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, documento: valor }));
                    }}
                  />
                </Box>
              </SimpleGrid>

              {/* O instrumento em que o título é baixado. Vazio usa a Moeda do
          próprio banco, que é o comportamento de antes do vínculo
          múltiplo. */}
              <Box>
                <Text
                  as="label"
                  htmlFor="moeda"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Moeda
                </Text>
                <OktonSearchSelect
                  name="moeda"
                  placeholder="Moeda do banco (padrão)"
                  value={form.moeda}
                  onChange={valor => setForm(f => ({ ...f, moeda: valor }))}
                  options={[
                    ...opcoesMoeda.map(m => ({
                      value: m.id,
                      label: m.nome,
                    })),
                  ]}
                />
                <Text fontSize="xs" color="gray.500" mt="1">
                  É o que registra por onde o dinheiro entrou. Vincule mais
                  Moedas de Banco à Conta na tela de Contas Bancárias; as de
                  Cheque de Terceiro aparecem sempre, porque o cheque só chega a
                  uma Conta quando for depositado.
                </Text>
              </Box>

              {/* Cheque de Terceiro liquida o título com papéis, e eles são
              cadastrados em tela própria. Nada é gravado lá: os cheques
              voltam para cá e entram junto com o lançamento. */}
              {ehCheque && (
                <Box borderWidth="1px" rounded="2xl" p="4">
                  <Flex align="center" gridGap="3" flexWrap="wrap">
                    <Button
                      leftIcon={<Wallet size={18} />}
                      colorScheme="blue"
                      variant="outline"
                      borderWidth="2px"
                      onClick={() =>
                        history.push('/lancamentos/novo/cheques', {
                          conta: form.conta,
                          form,
                          cheques,
                          valor: form.valor,
                          voltarPara: state?.voltarPara,
                          periodo: state?.periodo,
                        })
                      }
                    >
                      {cheques.length > 0
                        ? `${cheques.length} cheque(s)`
                        : 'Cadastrar cheques'}
                    </Button>
                    {cheques.length > 0 && (
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={chequesFecham ? 'green.600' : 'orange.500'}
                      >
                        {formatValue(somaCheques)}
                      </Text>
                    )}
                  </Flex>
                  <Text fontSize="xs" color="gray.500" mt="2">
                    {chequesFecham
                      ? 'A soma dos cheques fecha com o valor do lançamento.'
                      : 'A soma dos cheques precisa fechar com o valor do lançamento para salvar.'}
                  </Text>
                </Box>
              )}

              <Box>
                <Text
                  as="label"
                  htmlFor="cliente"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Cliente
                </Text>
                {/* A busca fica dentro do próprio seletor: o cadastro de
            Clientes passa de milhares de linhas em base de produção, então
            a lista vem do servidor conforme se digita. */}
                <OktonSearchSelect
                  filterOptions={false}
                  name="cliente"
                  value={form.cliente}
                  onChange={cliente => setForm(f => ({ ...f, cliente }))}
                  search={buscaCliente}
                  onSearchChange={setBuscaCliente}
                  loading={isClientesFetching}
                  placeholder="Nome, código ou CPF/CNPJ"
                  emptyMessage={
                    buscaCliente.trim().length < 2
                      ? 'Digite ao menos 2 caracteres para buscar'
                      : 'Nenhum cliente encontrado'
                  }
                  options={[
                    { value: CLIENTE_NOVO, label: 'Novo Cliente' },
                    ...clientes.map(c => ({
                      value: c.id_cli,
                      label: c.nome_cli,
                    })),
                  ]}
                />
              </Box>

              {form.cliente === CLIENTE_NOVO && (
                <Box borderWidth="1px" rounded="2xl" p="4">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                    mb="3"
                  >
                    Novo Cliente
                  </Text>
                  <Stack spacing="4">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb="1">
                        Tipo *
                      </Text>
                      <HStack overflow="hidden" spacing={2} p="1">
                        <Button
                          flex="1"
                          borderRadius="none"
                          variant={
                            form.clientenovotipo === TIPO_CLIENTE_PF
                              ? 'solid'
                              : 'outline'
                          }
                          onClick={() =>
                            setForm(f => ({
                              ...f,
                              clientenovotipo: TIPO_CLIENTE_PF,
                            }))
                          }
                        >
                          Pessoa Física
                        </Button>
                        <Button
                          flex="1"
                          borderRadius="none"
                          variant={
                            form.clientenovotipo === TIPO_CLIENTE_PJ
                              ? 'solid'
                              : 'outline'
                          }
                          onClick={() =>
                            setForm(f => ({
                              ...f,
                              clientenovotipo: TIPO_CLIENTE_PJ,
                            }))
                          }
                        >
                          Pessoa Jurídica
                        </Button>
                      </HStack>
                    </Box>
                    <Box>
                      <Text
                        as="label"
                        htmlFor="lancamento-clientenovonome"
                        display="block"
                        fontSize="sm"
                        fontWeight="medium"
                        mb="1"
                      >
                        Nome *
                      </Text>
                      <OktonInput
                        name="lancamento-clientenovonome"
                        color={color}
                        maxLength={60}
                        value={form.clientenovonome}
                        onChange={e => {
                          const valor = e.target.value;
                          nomeClienteDigitadoRef.current = true;
                          setForm(f => ({
                            ...f,
                            clientenovonome: valor,
                          }));
                        }}
                      />
                    </Box>
                    <Box>
                      <Text
                        as="label"
                        htmlFor="lancamento-clientenovodocumento"
                        display="block"
                        fontSize="sm"
                        fontWeight="medium"
                        mb="1"
                      >
                        {form.clientenovotipo === TIPO_CLIENTE_PJ
                          ? 'CNPJ *'
                          : 'CPF'}
                      </Text>
                      <OktonInput
                        name="lancamento-clientenovodocumento"
                        color={color}
                        maxLength={18}
                        placeholder={
                          form.clientenovotipo === TIPO_CLIENTE_PJ
                            ? 'Só números'
                            : 'Opcional'
                        }
                        value={formataDocumento(form.clientenovodocumento)}
                        onChange={e => {
                          const valor = e.target.value;
                          setForm(f => ({
                            ...f,
                            clientenovodocumento: soDigitos(valor),
                          }));
                        }}
                      />
                      {form.clientenovotipo === TIPO_CLIENTE_PJ && (
                        <Text fontSize="xs" color="gray.500" mt="1">
                          {enderecoNovoCliente
                            ? 'Endereço encontrado e será cadastrado junto com o Cliente.'
                            : 'Ao completar o CNPJ, o nome e o endereço são consultados automaticamente.'}
                        </Text>
                      )}
                      {clienteComDocumento?.existe &&
                        clienteComDocumento.cliente && (
                          <Text fontSize="xs" color="orange.500" mt="1">
                            Este documento já pertence ao Cliente &quot;
                            {clienteComDocumento.cliente.nome_cli}&quot; (código{' '}
                            {clienteComDocumento.cliente.id_cli}). Busque por
                            ele no campo Cliente, em vez de cadastrar um novo.
                          </Text>
                        )}
                    </Box>
                    {form.clientenovotipo === TIPO_CLIENTE_PJ && (
                      <Box>
                        <Text
                          as="label"
                          htmlFor="lancamento-clientenovoie"
                          display="block"
                          fontSize="sm"
                          fontWeight="medium"
                          mb="1"
                        >
                          Inscrição Estadual
                        </Text>
                        <OktonInput
                          name="lancamento-clientenovoie"
                          color={color}
                          maxLength={15}
                          placeholder="Opcional — sem informar, o Cliente é gravado como Isento"
                          value={form.clientenovoie}
                          onChange={e => {
                            const valor = e.target.value;
                            setForm(f => ({
                              ...f,
                              clientenovoie: valor,
                            }));
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>

            <Stack spacing="4">
              <Box>
                <Text
                  as="label"
                  htmlFor="direcionador"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Direcionador {controlaDRE && '*'}
                </Text>
                <OktonSearchSelect
                  name="direcionador"
                  placeholder="Sem direcionador"
                  value={form.direcionador}
                  onChange={direcionador =>
                    setForm(f => ({ ...f, direcionador }))
                  }
                  options={[
                    { value: DIRECIONADOR_NOVO, label: 'Novo Direcionador' },
                    ...direcionadores.map(d => ({
                      value: d.id_ori,
                      label: d.nome_ori,
                    })),
                  ]}
                />
                {controlaDRE && (
                  <Text fontSize="xs" color="gray.500" mt="1">
                    Obrigatório com o parâmetro Controla DRE ativo: é dele que
                    sai a classificação do Fluxo de Caixa.
                  </Text>
                )}
              </Box>

              {form.direcionador === DIRECIONADOR_NOVO && (
                <>
                  <Box>
                    <Text
                      as="label"
                      htmlFor="lancamento-direcionadornovo"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      mb="1"
                    >
                      Nome do novo Direcionador *
                    </Text>
                    <OktonInput
                      name="lancamento-direcionadornovo"
                      color={color}
                      maxLength={120}
                      placeholder="Ex.: TARIFAS BANCÁRIAS"
                      value={form.direcionadornovo}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({
                          ...f,
                          direcionadornovo: valor,
                        }));
                      }}
                    />
                    <Text fontSize="xs" color="gray.500" mt="1">
                      Cadastrado com Tipo Outros, Tipo de Venda Normal e
                      Sugestão de Preço Preço de Venda.
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      as="label"
                      htmlFor="direcionadorplano"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      mb="1"
                    >
                      Plano de Contas (DFC) do Direcionador {controlaDRE && '*'}
                    </Text>
                    <OktonSearchSelect
                      name="direcionadorplano"
                      placeholder="Sem classificação"
                      value={form.direcionadorplano}
                      onChange={valor =>
                        setForm(f => ({
                          ...f,
                          direcionadorplano: valor,
                        }))
                      }
                      options={[
                        ...planosDFC.map(p => ({
                          value: p.id_pct,
                          label: `${p.item_pct} ${p.nome_pct}`,
                        })),
                      ]}
                    />
                  </Box>
                </>
              )}

              <Box>
                <Text
                  as="label"
                  htmlFor="departamento"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Departamento
                </Text>
                <OktonSearchSelect
                  name="departamento"
                  placeholder="Sem departamento"
                  value={form.departamento}
                  onChange={departamento =>
                    setForm(f => ({ ...f, departamento }))
                  }
                  options={[
                    { value: DEPARTAMENTO_NOVO, label: 'Novo Departamento' },
                    ...departamentos.map(d => ({
                      value: d.id_dpt,
                      label: d.nome_dpt,
                    })),
                  ]}
                />
              </Box>

              {form.departamento === DEPARTAMENTO_NOVO && (
                <Box>
                  <Text
                    as="label"
                    htmlFor="lancamento-departamentonovo"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Nome do novo Departamento *
                  </Text>
                  <OktonInput
                    name="lancamento-departamentonovo"
                    color={color}
                    maxLength={60}
                    placeholder="Ex.: ADMINISTRATIVO"
                    value={form.departamentonovo}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, departamentonovo: valor }));
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt="1">
                    É cadastrado no IFLEX junto com o lançamento.
                  </Text>
                </Box>
              )}

              <Box>
                <Text
                  as="label"
                  htmlFor="centrocusto"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Centro de Custo
                </Text>
                <OktonSearchSelect
                  name="centrocusto"
                  placeholder="Sem centro de custo"
                  value={form.centrocusto}
                  onChange={centrocusto =>
                    setForm(f => ({ ...f, centrocusto }))
                  }
                  options={[
                    {
                      value: CENTRO_CUSTO_NOVO,
                      label: 'Novo Centro de Custo',
                    },
                    ...centrosCusto.map(c => ({
                      value: c.id_cdc,
                      label: c.nome_cdc,
                    })),
                  ]}
                />
              </Box>

              {form.centrocusto === CENTRO_CUSTO_NOVO && (
                <Box>
                  <Text
                    as="label"
                    htmlFor="lancamento-centrocustonovo"
                    display="block"
                    fontSize="sm"
                    fontWeight="medium"
                    mb="1"
                  >
                    Nome do novo Centro de Custo *
                  </Text>
                  <OktonInput
                    name="lancamento-centrocustonovo"
                    color={color}
                    maxLength={60}
                    placeholder="Ex.: TARIFAS BANCÁRIAS"
                    value={form.centrocustonovo}
                    onChange={e => {
                      const valor = e.target.value;
                      setForm(f => ({ ...f, centrocustonovo: valor }));
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt="1">
                    É cadastrado no IFLEX junto com o lançamento.
                  </Text>
                </Box>
              )}

              <Flex gridGap="3" pt="4">
                <Button variant="ghost" onClick={voltar}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  leftIcon={
                    saving ? <Spinner size="sm" /> : <Check size={16} />
                  }
                  isDisabled={saving}
                  onClick={salvar}
                >
                  Adicionar
                </Button>
              </Flex>
            </Stack>
          </SimpleGrid>
        </Box>
      </Box>
    </BankAppPage>
  );
};

export default NovoLancamento;
